import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  CheckDuplicateProductCodeQuery,
  CreateProductFromStandardDataBody,
  FindFreePolicyProductsBody,
  FindFreePricePolicyProductsQuery,
  FindFreeProposalPolicyProductsQuery,
  FindProductOptions,
  GenerateProductCodeQuery,
  ProductApi,
} from 'api';
import { product as productQueryInit } from 'containers/ProductionManager/ProductManager/Product/commons/query';

import { RootState } from '../app';
import {
  findProductsForTableList,
  findProductsForReportQuantity,
  findProductsForSingleSelect,
} from './entityProductSlice';

export const formatProductDataForTable = (product: any) => {
  const {
    id,
    name,
    avatar,
    productOptions,
    slug,
    retailUnit,
    wholesaleUnit,
    code,
  } = product;
  return {
    ...product,
    key: product.id,
    infoBox: {
      id,
      slug,
      name,
      avatar,
      productOptions,
      retailUnit,
      wholesaleUnit,
      code,
    },
  };
};

export const checkProductInMarketing = createAsyncThunk(
  'product/checkProductInMarketing',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await ProductApi.checkProductInMarketing(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkCanDeleteProduct = createAsyncThunk(
  'product/checkCanDeleteProduct',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await ProductApi.checkCanDeleteProduct(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findFreeProposalPolicyProducts = createAsyncThunk<
  any,
  {
    query: FindFreeProposalPolicyProductsQuery;
    body: FindFreePolicyProductsBody;
  },
  { rejectValue: { message: string } }
>(
  'product/findFreeProposalPolicyProducts',
  async ({ query, body }, { rejectWithValue }) => {
    try {
      const response = await ProductApi.findFreeProposalPolicyProducts(
        query,
        body
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findFreePricePolicyProducts = createAsyncThunk<
  any,
  { query: FindFreePricePolicyProductsQuery; body: FindFreePolicyProductsBody },
  { rejectValue: { message: string } }
>(
  'product/findFreePricePolicyProducts',
  async ({ query, body }, { rejectWithValue }) => {
    try {
      const response = await ProductApi.findFreePricePolicyProducts(
        query,
        body
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductToSelect = createAsyncThunk(
  'product/findProductToSelect',
  async (options: FindProductOptions, { rejectWithValue }) => {
    try {
      const response = await ProductApi.find(options);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProduct = createAsyncThunk(
  'product/findProduct',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await ProductApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPricePolicyForProduct = createAsyncThunk<
  any,
  { option: any; body: any },
  { rejectValue: { message: string } }
>(
  'product/findPricePolicyForProduct',
  async ({ option, body }, { rejectWithValue }) => {
    try {
      const response = await ProductApi.findProductPricePolicy({
        option,
        body,
      });

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductProposalItem = createAsyncThunk<
  any,
  { option: any; body: any },
  { rejectValue: { message: string } }
>(
  'product/findProductProposalItem',
  async ({ option, body }, { rejectWithValue }) => {
    try {
      const response = await ProductApi.findProductProposalItem({
        option,
        body,
      });

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductProposalItemsSupplier = createAsyncThunk<
  any,
  { option: any; body: any },
  { rejectValue: { message: string } }
>(
  'product/findProductProposalItemsSupplier',
  async ({ option, body }, { rejectWithValue }) => {
    try {
      const response = await ProductApi.findProductProposalItemsSupplier({
        option,
        body,
      });

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductProposalItemsCustomer = createAsyncThunk<
  any,
  { option: any; body: any },
  { rejectValue: { message: string } }
>(
  'product/findProductProposalItemsCustomer',
  async ({ option, body }, { rejectWithValue }) => {
    try {
      const response = await ProductApi.findProductProposalItemsCustomer({
        option,
        body,
      });

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductProposalItemsToOrder = createAsyncThunk<
  any,
  { options: FindProductOptions; ownerID: string; supplierIDs: string[] },
  { rejectValue: { message: string } }
>(
  'product/findProposalItems',
  async ({ options, ownerID, supplierIDs }, { rejectWithValue }) => {
    try {
      const response = await ProductApi.findProposalItemsToOrder(
        options,
        ownerID,
        supplierIDs
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const duplicateProducts = createAsyncThunk<
  any,
  { products: { id: string; name?: string }[] },
  { rejectValue: { message: string } }
>('product/duplicateProducts', async ({ products }, { rejectWithValue }) => {
  try {
    const response = await ProductApi.duplicate({ products });
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const checkCanDeleteMultiProducts = createAsyncThunk<
  any,
  { productIDs: string[] },
  { rejectValue: { message: string } }
>('product/checkCanDeleteMultiProducts', async (body, { rejectWithValue }) => {
  try {
    const response = await ProductApi.checkCanDeleteMultiProducts(body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const deleteMultiProducts = createAsyncThunk<
  any,
  { productIDs: string[] },
  { rejectValue: { message: string } }
>('product/deleteMultiProducts', async (body, { rejectWithValue }) => {
  try {
    const response = await ProductApi.deleteMulti(body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findProductOrders = createAsyncThunk<
  any,
  {
    options: {
      name?: string;
      categoryID?: string;
      page?: number;
      take?: number;
    };
    ownerID: string;
    customerIDs: string[];
    orderType: number;
    coBuyIDs?: string[];
    startedAt?: string;
    finishedAt?: string;
  },
  { rejectValue: { message: string } }
>(
  'product/findOrders',
  async (
    {
      options,
      ownerID,
      customerIDs,
      orderType,
      coBuyIDs,
      startedAt,
      finishedAt,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await ProductApi.findOrders(
        options,
        ownerID,
        customerIDs,
        orderType,
        coBuyIDs || [],
        startedAt,
        finishedAt
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createProduct = createAsyncThunk(
  'product/createProduct',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await ProductApi.create(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createImportProduct = createAsyncThunk(
  'product/createImportProduct',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await ProductApi.createImport(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createProductFromStandardData = createAsyncThunk(
  'product/createProductFromStandardData',
  async (body: CreateProductFromStandardDataBody, { rejectWithValue }) => {
    try {
      const response = await ProductApi.createFromStandardData(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOneProduct = createAsyncThunk(
  'product/findOneProduct',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, option } = params;
      const response = await ProductApi.findOne(id, option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeProduct = createAsyncThunk(
  'product/removeProduct',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await ProductApi.remove(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface Update {
  id: string;
  body: any;
}
export const updateProduct = createAsyncThunk(
  'product/update',
  async (params: Update, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await ProductApi.update(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProductMultiByAdmin = createAsyncThunk(
  'product/update',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await ProductApi.updateMulti(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProductPrice = createAsyncThunk(
  'product/updatePrice',
  async (params: Update, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await ProductApi.updatePrice(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProductQuantity = createAsyncThunk(
  'product/updateQuantity',
  async (params: Update, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await ProductApi.updateQuantity(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProductStatus = createAsyncThunk(
  'product/updateStatus',
  async (params: Update, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await ProductApi.updateStatus(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createProductCoBuyPromotion = createAsyncThunk(
  'product/createCoBuyPromotion',
  async (params: Update, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await ProductApi.createCoBuyPromotion(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProductCoBuyPromotion = createAsyncThunk(
  'product/updateCoBuyPromotion',
  async (
    params: { id: string; promotionId: string; body: any },
    { rejectWithValue }
  ) => {
    try {
      const { id, body, promotionId } = params;
      const response = await ProductApi.updateCoBuyPromotion(
        id,
        promotionId,
        body
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeProductCoBuyPromotion = createAsyncThunk(
  'product/removeCoBuyPromotion',
  async (params: { id: string; promotionId: string }, { rejectWithValue }) => {
    try {
      const { id, promotionId } = params;
      const response = await ProductApi.removeCoBuyPromotion(id, promotionId);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const findSuggestProductsForProductAutoSynced = createAsyncThunk(
  'product/findSuggestProductsForProductAutoSynced',
  async (
    option: {
      page?: number;
      take?: number;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await ProductApi.findSuggestProductsForProductAutoSynced(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findSelectedProduct = createAsyncThunk(
  'product/findSelectedProduct',
  async (
    params: { body: { productIDs: string[] }; option: any },
    { rejectWithValue }
  ) => {
    try {
      const { body, option } = params;
      const response = await ProductApi.findSelectedProduct(body, option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkDuplicateProductCode = createAsyncThunk(
  'area/checkDuplicateProductCode',
  async (query: CheckDuplicateProductCodeQuery, { rejectWithValue }) => {
    try {
      const response = await ProductApi.checkDuplicate(query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const generateProductCode = createAsyncThunk(
  'area/generateProductCode',
  async (query: GenerateProductCodeQuery, { rejectWithValue }) => {
    try {
      const response = await ProductApi.generateCode(query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface State {
  waitingApprovalProducts: Array<any>;
  tableList: Array<any>;
  orderList: Array<any>;
  list: Array<any>;
  reloadFlag: boolean;
  resetFlag: boolean;
  query: any;
  savedPagination?: any;
  savedList: Array<any>;
}

const initialState: State = {
  tableList: [],
  waitingApprovalProducts: [],
  orderList: [],
  list: [],
  reloadFlag: false,
  resetFlag: false,
  savedPagination: { current: 1, next: 2, prev: 0, take: 10, total: 0 },
  savedList: [],
  query: productQueryInit,
};

const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    reloadProduct(state) {
      state.reloadFlag = !state.reloadFlag;
    },
    resetProductList(state) {
      state.resetFlag = !state.resetFlag;
    },
    clearWaitingApprovalProducts(state) {
      state.waitingApprovalProducts = [];
    },
    updateSavedPagination(state, action) {
      state.savedPagination = action.payload;
    },
    updateSavedListProduct(state, action) {
      state.savedList = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(findProductsForTableList.fulfilled, (state, action) => {
        const { data } = action.payload;
        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
            infoBox: {
              name: item.name,
              code: item.code,
              avatar: item.avatar,
            },
          };
        });

        state.tableList = newData;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        const { data } = action.payload;

        state.waitingApprovalProducts.push(data);
      })
      .addCase(findProductsForReportQuantity.fulfilled, (state, action) => {
        const { data } = action.payload;

        const newData = data.map((item: any) => {
          const { id, name, avatar, productOptions, slug } = item;
          return {
            ...item,
            key: item.id,
            infoBox: {
              id,
              slug,
              name,
              avatar,
              productOptions,
            },
          };
        });
        state.orderList = newData;
      })
      .addCase(findProduct.fulfilled, (state, action) => {
        const { data } = action.payload;
        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
            infoBox: {
              name: item.name,
              code: item.code,
              avatar: item.avatar,
              productOptions: item.productOptions,
            },
          };
        });

        state.list = newData;
      })
      .addCase(findProductsForSingleSelect.fulfilled, (state, action) => {
        const { data } = action.payload;
        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
            infoBox: {
              name: item.name,
              code: item.code,
              avatar: item.avatar,
              productOptions: item.productOptions,
            },
          };
        });

        state.list = newData;
      });
  },
});

const { actions, reducer: productReducer } = productSlice;

export const {
  reloadProduct,
  resetProductList,
  updateSavedPagination,
  updateSavedListProduct,
  clearWaitingApprovalProducts,
} = actions;

export default productReducer;

export const selectProduct = (state: RootState): State =>
  state.product as State;
